import { Circle, CaretUp, MagnifyingGlass, X } from '@phosphor-icons/react';
import React, { useState, useRef, useEffect } from 'react';

interface PodOption {
  id: string;
  name: string;
  reference_num: string;
}

interface PodDropdownProps {
  availablePods: PodOption[];
  selectedPod: string;
  setSelectedPod: (id: string) => void;
  podLatestReportEvaluated: Record<string, boolean>;
  isAdmin?: boolean;
}

export const PodDropdown: React.FC<PodDropdownProps> = ({
  availablePods,
  selectedPod,
  setSelectedPod,
  podLatestReportEvaluated,
  isAdmin = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const selected = availablePods.find((p) => p.id === selectedPod);

  const filteredPods =
    isAdmin && searchTerm
      ? availablePods.filter(
          (pod) =>
            pod.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pod.reference_num.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : availablePods;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border bg-white px-4 py-2 font-inter text-sm focus:outline-none focus:ring-0"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {selected ? (
            <>
              {podLatestReportEvaluated[selected.id] === false && (
                <Circle size={10} weight="fill" className="text-red-500" />
              )}
              {selected.name} ({selected.reference_num})
            </>
          ) : (
            'Select Pod'
          )}
        </span>
        <CaretUp
          size={18}
          className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white py-1 shadow-lg focus:outline-none"
          tabIndex={-1}
          role="listbox"
        >
          {isAdmin && (
            <li className="px-4 py-2">
              <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-2 py-1">
                <MagnifyingGlass size={16} weight="bold" />
                <input
                  type="text"
                  className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                  placeholder="Search pods..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <span
                    className="cursor-pointer hover:text-brandRed-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                  >
                    <X size={16} weight="bold" />
                  </span>
                )}
              </div>
            </li>
          )}
          {filteredPods.length === 0 && (
            <li
              className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100`}
            >
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {isAdmin && searchTerm
                    ? 'No pods match your search'
                    : 'No Pods Available'}
                </span>
              </span>
            </li>
          )}
          {filteredPods.map((pod) => (
            <li
              key={pod.id}
              className={`flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100 ${
                pod.id === selectedPod ? 'bg-primary-50 font-semibold' : ''
              }`}
              onClick={() => {
                setSelectedPod(pod.id);
                setOpen(false);
                setSearchTerm('');
              }}
              role="option"
              aria-selected={pod.id === selectedPod}
            >
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {pod.name} ({pod.reference_num})
                </span>
                {podLatestReportEvaluated[pod.id] === false && (
                  <Circle size={10} weight="fill" className="text-red-500" />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
